// LAP-551 — Fabriques de colonnes DataGrid pour les listes de valeurs.
//
// POURQUOI CE FICHIER EXISTE
// Jusqu'ici les colonnes numériques des listes Investissements formataient la
// valeur DANS `valueGetter` :
//     { field: 'price', valueGetter: (p) => numCell(p.row.price) }   // "1 234,56"
// Or le DataGrid trie ET filtre sur ce que renvoie `valueGetter`. On triait
// donc des CHAÎNES : « 1000 » passait avant « 99 », « 1.2 Mds » avant
// « 980 k », et les tirets « — » des valeurs vides se retrouvaient en tête.
// Le menu de colonne proposait en prime les opérateurs du texte (« contient »)
// au lieu de ceux des nombres (>, <, =).
//
// LA RÈGLE, désormais valable pour toutes les listes de l'app :
//   • `valueGetter`    → la valeur BRUTE (nombre, date ou null) : c'est elle
//                        qui sert au tri, au filtre et à la recherche ;
//   • `valueFormatter` → l'affichage (devise, unités, séparateurs, « — ») ;
//   • `type`           → 'number' / 'date' pour que le DataGrid propose les
//                        bons opérateurs de filtre et le bon comparateur.
import React from 'react'
import Delta from './Delta'

export const EMPTY = '—'

/** Convertit en nombre ce que renvoie l'API (nombre, chaîne décimale, null). */
export const toNum = (v) => {
  if (v === undefined || v === null || v === '') return null
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'))
  return Number.isNaN(n) ? null : n
}

/** Convertit en Date une valeur ISO ; null si absente/invalide. */
export const toDate = (v) => {
  if (!v) return null
  const d = v instanceof Date ? v : new Date(v)
  return Number.isNaN(d.getTime()) ? null : d
}

export const formatNumber = (v, decimals = 2) =>
  v === null || v === undefined ? EMPTY : Number(v).toFixed(decimals)

export const formatMoney = (v, currency = '€') =>
  v === null || v === undefined
    ? EMPTY
    : `${Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${currency}`

export const formatCompact = (v) => {
  if (v === null || v === undefined) return EMPTY
  const n = Number(v)
  if (Math.abs(n) >= 1e9) return `${(n / 1e9).toFixed(2)} Mds`
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(2)} M`
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(1)} k`
  return n.toLocaleString('fr-FR')
}

/** Colonne texte — explicite pour rester homogène avec les autres fabriques. */
export const textCol = ({ field, headerName, ...rest }) => ({
  field,
  headerName,
  type: 'string',
  ...rest,
})

const numericBase = ({ field, headerName, getter, ...rest }) => ({
  field,
  headerName,
  type: 'number',
  headerAlign: 'right',
  align: 'right',
  valueGetter: getter || ((p) => toNum(p.row[field])),
  ...rest,
})

/** Nombre brut, N décimales (prix, PER, score, duration…). */
export const numberCol = ({ field, headerName, decimals = 2, ...rest }) =>
  numericBase({
    field,
    headerName,
    valueFormatter: ({ value }) => formatNumber(value, decimals),
    ...rest,
  })

/** Montant en devise (« 1 234,56 € »). */
export const moneyCol = ({ field, headerName, currency = '€', ...rest }) =>
  numericBase({
    field,
    headerName,
    valueFormatter: ({ value }) => formatMoney(value, currency),
    ...rest,
  })

/** Grand nombre abrégé (« 1.24 Mds », « 980 k ») — volumes, capitalisations. */
export const compactCol = ({ field, headerName, ...rest }) =>
  numericBase({
    field,
    headerName,
    valueFormatter: ({ value }) => formatCompact(value),
    ...rest,
  })

/** Pourcentage simple (coupon, rendement) — affiché « 3.20 % », trié en nombre. */
export const percentCol = ({ field, headerName, decimals = 2, ...rest }) =>
  numericBase({
    field,
    headerName,
    valueFormatter: ({ value }) =>
      value === null || value === undefined ? EMPTY : `${Number(value).toFixed(decimals)} %`,
    ...rest,
  })

/**
 * Variation colorée (composant Delta). `kind: 'delta'` est conservé : la page
 * détail Marché s'en sert pour masquer les valeurs (bouton œil).
 */
export const deltaCol = ({ field, headerName, unit = '%', ...rest }) =>
  numericBase({
    field,
    headerName,
    kind: 'delta',
    unit,
    renderCell: (p) =>
      p.value === null || p.value === undefined ? EMPTY : <Delta value={p.value} unit={unit} />,
    ...rest,
  })

/** Date — triée et filtrée comme une date, affichée en JJ/MM/AAAA. */
export const dateCol = ({ field, headerName, fallbackFields = [], ...rest }) => ({
  field,
  headerName,
  type: 'date',
  valueGetter: (p) => {
    const raw = [field, ...fallbackFields].map((f) => p.row[f]).find((v) => v)
    return toDate(raw)
  },
  valueFormatter: ({ value }) =>
    value ? value.toLocaleDateString('fr-FR') : EMPTY,
  ...rest,
})
